import { useState, useEffect } from "react";
import validator from "validator";
import { Link, Navigate, useSearchParams, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { FiMail, FiLock, FiUser, FiEye, FiEyeOff } from "react-icons/fi";

import Loader from "@/components/loader";

import api from "@/services/api";
import store from "@/services/store";

const Invite = () => {
  const [values, setValues] = useState({ email: "", password: "", firstname: "", lastname: "" });
  const [errors, setErrors] = useState({ email: "", password: "", firstname: "", lastname: "" });
  const [loading, setLoading] = useState(false);
  const [seePassword, setSeePassword] = useState(false);
  const [searchParams] = useSearchParams();
  const { user, setUser, setOrganization } = store();
  const navigate = useNavigate();
  const redirect = searchParams.get("redirect");
  const inviteToken = searchParams.get("token");

  async function getUser() {
    try {
      const { ok, user, code } = await api.post("/user/invitation_verify", { invitation_token: inviteToken });
      if (!ok) throw new Error(code);
      setValues({ ...values, email: user.email });
    } catch (e) {
      console.error(e);
      toast.error(e.code);
    }
  }

  useEffect(() => {
    if (inviteToken) getUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inviteToken]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setValues({ ...values, [name]: value });
    setErrors({ ...errors, [name]: "" });
  };

  const handleSubmit = async (e) => {
    try {
      e.preventDefault();

      if (!values.firstname) return setErrors({ ...errors, firstname: "First name is required" });
      if (!values.lastname) return setErrors({ ...errors, lastname: "Last name is required" });
      if (!values.password) return setErrors({ ...errors, password: "Password is required" });
      if (!validator.isEmail(values?.email)) return toast.error("Please enter a valid email address");

      setLoading(true);

      const data = {
        email: values.email,
        password: values.password,
        firstname: values.firstname,
        lastname: values.lastname,
        invitation_token: inviteToken,
      };

      const { ok, data: userData, token, code, organisations } = await api.post("/user/invitation_signup", data);
      if (!ok) {
        toast.error(code || "Failed to create account");
        setLoading(false);
        return;
      }

      if (token) api.setToken(token);
      if (userData) {
        setUser(userData);
        setOrganization(organisations?.[0] || null);
        toast.success("Your account has been created successfully!");
        if (redirect) navigate(redirect);
        else navigate("/");
      }
    } catch (e) {
      console.error("error", e);
      if (e.code === "USER_ALREADY_REGISTERED") return toast.error(e.code);
      toast.error(e.code);
    } finally {
      setLoading(false);
    }
  };

  if (user) return <Navigate to={"/"} />;

  if (!redirect && !inviteToken) {
    toast.error("Invalid invitation link");
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="relative max-w-xl w-full">
          <div className="relative bg-white rounded-xl border border-blue-500/20 shadow-2xl p-10">
            <div className="text-center mb-6">
              <div className="flex items-center justify-center mb-6">
                <div className="flex items-center">
                  <div className="mb-8 flex justify-center">
                    <span className="text-xl font-extrabold text-gray-900">Jobego</span>
                  </div>
                </div>
              </div>
              <h2 className="text-2xl font-semibold text-gray-900">Invitation</h2>
            </div>
            <div className="mt-6 p-6 bg-gray-50 rounded-lg border border-gray-200 text-center">
              <p className="text-gray-700 mt-2 font-medium">You've got an invalid invitation link.</p>
              <p className="text-gray-600 mt-2 text-sm">👉 Please contact the project owner to get a new one</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex items-center justify-center">
      <div className="relative max-w-xl w-full">
        <div className="relative bg-white rounded-xl border border-blue-500/20 shadow-2xl p-10">
          <form className="w-full" onSubmit={handleSubmit}>
            <div className="text-center mb-6">
              <div className="flex items-center justify-center mb-6">
                <div className="flex items-center">
                  <div className="mb-8 flex justify-center">
                    <span className="text-xl font-extrabold text-gray-900">Jobego</span>
                  </div>
                </div>
              </div>
              <h1 className="text-2xl font-semibold text-gray-900">Invitation to Jobego</h1>
              <p className="text-gray-600 text-sm mt-4">Complete your registration to get started</p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-sm text-gray-700 flex items-center mb-1">
                  <FiMail className="mr-2 text-blue-400" size={18} />
                  Work email
                </label>
                <input
                  className="w-full px-3 py-2 rounded-lg bg-gray-100 border border-gray-300 text-gray-600 text-sm"
                  name="email"
                  type="email"
                  id="email"
                  value={values.email}
                  disabled
                  placeholder="example@mail.com"
                />
                {errors.email && <p className="text-xs text-red-400 mt-1">{errors.email}</p>}
              </div>
              <div>
                <label className="text-sm text-gray-700 flex items-center mb-1">
                  <FiUser className="mr-2 text-blue-400" size={18} />
                  Full Name <span className="text-blue-400 ml-1">*</span>
                </label>
                <div className="flex items-center gap-2 w-full">
                  <input
                    className="w-full px-3 py-2 rounded-lg bg-white border border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500/20 transition-all text-gray-900 text-sm"
                    name="firstname"
                    type="text"
                    value={values.firstname}
                    onChange={handleChange}
                    placeholder="Enter your first name"
                  />
                  <input
                    className="w-full px-3 py-2 rounded-lg bg-white border border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500/20 transition-all text-gray-900 text-sm"
                    name="lastname"
                    type="text"
                    value={values.lastname}
                    onChange={handleChange}
                    placeholder="Enter your surname"
                  />
                </div>
                <div className="flex flex-col">
                  {errors.firstname && <p className="text-xs text-red-400 mt-1">{errors.firstname}</p>}
                  {errors.lastname && <p className="text-xs text-red-400 mt-1">{errors.lastname}</p>}
                </div>
              </div>
              <div>
                <label className="text-sm text-gray-700 flex items-center mb-1">
                  <FiLock className="mr-2 text-blue-400" size={18} />
                  Password <span className="text-blue-400 ml-1">*</span>
                </label>
                <div className="relative">
                  <input
                    className="w-full px-3 py-2 rounded-lg bg-white border border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500/20 transition-all text-gray-900 text-sm"
                    name="password"
                    type={seePassword ? "text" : "password"}
                    id="password"
                    value={values.password}
                    onChange={handleChange}
                    placeholder="Choose a secure password"
                  />
                  <button type="button" onClick={() => setSeePassword(!seePassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-blue-400">
                    {seePassword ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                  </button>
                </div>
                {errors.password && <p className="text-xs text-red-400 mt-1">{errors.password}</p>}
              </div>
              <div className="bg-blue-50 p-2 rounded-lg border border-blue-200 text-xs text-gray-700">
                By signing up, you agree to our{" "}
                <a href="https://jobego.co/terms-privacy" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">
                  Terms and Privacy Policy
                </a>
              </div>
              <button className="w-full py-2 rounded-lg text-sm font-semibold text-white transition-all bg-blue-600 hover:bg-blue-700" type="submit" disabled={loading}>
                {loading ? <Loader size="small" color="white" /> : "Create Account"}
              </button>
              {!inviteToken && (
                <div className="w-full flex justify-center">
                  <Link className="w-full py-2 rounded-lg text-sm font-semibold text-gray-700 transition-all bg-gray-100 hover:bg-gray-200 text-center" to={`/auth/`}>
                    Already have an account
                  </Link>
                </div>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Invite;
